<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import Draggable from 'vuedraggable';

import PilotBuiltinToolsAPI from 'dashboard/api/pilot/builtinTools';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import AttributeListItem from 'dashboard/components-next/ConversationWorkflow/AttributeListItem.vue';
import EditAttribute from 'dashboard/routes/dashboard/settings/attributes/EditAttribute.vue';
import AddFieldDrawer from './AddFieldDrawer.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// 状态
const isLoading = ref(true);
const availableFields = ref([]);
const selectedFields = ref([]);
const addFieldDrawerRef = ref(null);
const [showEditPopup, toggleEditPopup] = useToggle(false);
const selectedAttribute = ref({});
const isDragging = ref(false);

// 常量
const MAX_FIELDS = 20;

// 图片路径
const toolThumbnail = '/assets/images/dashboard/pilot/wechat-light.svg';
const toolThumbnailDark = '/assets/images/dashboard/pilot/wechat-dark.svg';

// 假数据（用于空状态展示）
const placeholderFields = [
  {
    attribute_key: 'name',
    attribute_display_name: '姓名',
    attribute_description: '联系人姓名',
    type: 'text',
    value: 'name',
  },
  {
    attribute_key: 'phone',
    attribute_display_name: '电话',
    attribute_description: '联系人电话',
    type: 'text',
    value: 'phone',
  },
  {
    attribute_key: 'email',
    attribute_display_name: '邮箱',
    attribute_description: '联系人邮箱',
    type: 'text',
    value: 'email',
  },
];

// 计算属性
const isAtLimit = computed(() => selectedFields.value.length >= MAX_FIELDS);

// 为 AttributeListItem 格式化的字段数据
const formattedSelectedFields = computed(() =>
  selectedFields.value.map(field => ({
    ...field,
    label: field.attribute_display_name,
    type: field.attribute_display_type,
    value: field.attribute_key,
    description: field.attribute_description,
    is_deleted: field.is_deleted || false,
  }))
);

// 返回
const goBack = () => {
  router.push({ name: 'pilot_builtin_tools_index' });
};

// 打开添加字段抽屉
const openAddDrawer = () => {
  addFieldDrawerRef.value?.open();
};

// 批量添加字段
const handleBatchAdd = async fieldKeys => {
  try {
    await Promise.all(
      fieldKeys.map(key => PilotBuiltinToolsAPI.addContactField(key))
    );
    // 重新加载数据
    await loadData();
  } catch (error) {
    console.error('Failed to add fields:', error);
    useAlert(t('PILOT.BUILTIN_TOOLS.CONFIG.SAVE_ERROR'));
  }
};

// 加载数据
const loadData = async () => {
  isLoading.value = true;
  try {
    const response = await PilotBuiltinToolsAPI.getContactFields();
    availableFields.value = response.data.contact_fields || [];

    // 设置已选中的字段（包括已删除的字段）
    const selectedKeys = response.data.selected_fields || [];
    selectedFields.value = selectedKeys.map(key => {
      const field = availableFields.value.find(f => f.attribute_key === key);
      if (field) {
        return field;
      }
      // 字段已被删除，创建占位字段
      return {
        attribute_key: key,
        attribute_display_name: key,
        attribute_description: '',
        attribute_display_type: 'text',
        is_deleted: true,
      };
    });
  } catch (error) {
    console.error('Failed to load contact fields:', error);
    useAlert(t('PILOT.BUILTIN_TOOLS.CONFIG.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

// 添加字段
const handleAddField = async fieldKey => {
  if (!fieldKey || isAtLimit.value) return;

  const field = availableFields.value.find(f => f.attribute_key === fieldKey);
  if (!field) return;

  try {
    await PilotBuiltinToolsAPI.addContactField(fieldKey);
    selectedFields.value.push(field);
  } catch (error) {
    console.error('Failed to add field:', error);
    useAlert(t('PILOT.BUILTIN_TOOLS.CONFIG.SAVE_ERROR'));
  }
};

// 删除字段（从白名单移除，不是真正删除字段）
const handleRemoveField = async attribute => {
  try {
    await PilotBuiltinToolsAPI.removeContactField(attribute.attribute_key);
    selectedFields.value = selectedFields.value.filter(
      f => f.attribute_key !== attribute.attribute_key
    );
  } catch (error) {
    console.error('Failed to remove field:', error);
    useAlert(t('PILOT.BUILTIN_TOOLS.CONFIG.SAVE_ERROR'));
  }
};

// 编辑字段
const handleEditAttribute = attribute => {
  if (attribute.is_builtin) return;
  selectedAttribute.value = attribute;
  toggleEditPopup(true);
};

// 关闭编辑弹窗
const hideEditPopup = () => {
  toggleEditPopup(false);
  selectedAttribute.value = {};
};

// 字段编辑完成后刷新数据
const onAttributeUpdated = () => {
  hideEditPopup();
  loadData();
};

// 拖拽排序
const onDragStart = () => {
  isDragging.value = true;
};

const onDragEnd = async () => {
  isDragging.value = false;
  // 保存排序
  const fieldKeys = selectedFields.value.map(f => f.attribute_key);
  try {
    await PilotBuiltinToolsAPI.reorderContactFields(fieldKeys);
  } catch (error) {
    console.error('Failed to reorder fields:', error);
    useAlert(t('PILOT.BUILTIN_TOOLS.CONFIG.SAVE_ERROR'));
    // 失败时重新加载数据恢复原顺序
    loadData();
  }
};

onMounted(() => {
  loadData();
});
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-slate-white dark:bg-n-slate-800">
    <!-- Header -->
    <header class="sticky top-0 z-10 px-6 bg-n-slate-white dark:bg-n-slate-800">
      <div class="w-full mx-auto max-w-[60rem]">
        <div class="flex items-center h-20">
          <button
            type="button"
            class="p-2 -ml-2 text-n-slate-600 dark:text-n-slate-400 hover:bg-n-slate-100 dark:hover:bg-n-slate-700 rounded-lg transition-colors"
            @click="goBack"
          >
            <Icon icon="i-lucide-arrow-left" class="h-5 w-5" />
          </button>
          <h1
            class="ml-4 text-lg font-semibold text-n-slate-900 dark:text-n-slate-100"
          >
            {{ $t('PILOT.BUILTIN_TOOLS.CONFIG.TITLE') }}
          </h1>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="flex-1 w-full px-6 overflow-y-auto">
      <div class="w-full mx-auto max-w-[60rem] py-6 flex flex-col gap-6">
        <!-- Loading -->
        <div v-if="isLoading" class="flex items-center justify-center py-20">
          <Spinner />
        </div>

        <template v-else>
          <!-- 空字段警告提示 -->
          <div
            v-if="selectedFields.length === 0"
            class="flex items-start gap-3 p-4 rounded-xl border border-yellow-400 bg-yellow-50 dark:bg-yellow-900/30 dark:border-yellow-600"
          >
            <div
              class="flex-shrink-0 w-9 h-9 rounded-lg bg-yellow-100 dark:bg-yellow-800 flex items-center justify-center"
            >
              <Icon
                icon="i-lucide-alert-triangle"
                class="size-5 text-yellow-600 dark:text-yellow-400"
              />
            </div>
            <div class="text-sm">
              <p
                class="font-semibold text-yellow-800 dark:text-yellow-200 mb-1"
              >
                {{ $t('PILOT.BUILTIN_TOOLS.CONFIG.EMPTY_WARNING.TITLE') }}
              </p>
              <p class="text-yellow-700 dark:text-yellow-300 leading-relaxed">
                {{ $t('PILOT.BUILTIN_TOOLS.CONFIG.EMPTY_WARNING.DESCRIPTION') }}
              </p>
            </div>
          </div>

          <!-- 虚线介绍区域 -->
          <section class="custom-dashed-border rounded-2xl py-5 px-6">
            <div
              class="flex flex-col md:flex-row items-start md:items-center gap-6"
            >
              <div
                class="flex-shrink-0 w-[7.5rem] h-[6.5rem] rounded-lg flex items-center justify-center overflow-hidden bg-n-slate-2 dark:bg-n-slate-8"
              >
                <img
                  :src="toolThumbnail"
                  :alt="$t('PILOT.BUILTIN_TOOLS.CONFIG.GUIDE.TITLE')"
                  class="w-full h-full object-cover rounded-lg block dark:hidden"
                />
                <img
                  :src="toolThumbnailDark"
                  :alt="$t('PILOT.BUILTIN_TOOLS.CONFIG.GUIDE.TITLE')"
                  class="w-full h-full object-cover rounded-lg hidden dark:block"
                />
              </div>
              <div class="flex flex-col flex-1 gap-3">
                <p class="text-n-slate-12 text-sm font-medium">
                  {{ $t('PILOT.BUILTIN_TOOLS.CONFIG.GUIDE.TITLE') }}
                </p>
                <p class="text-n-slate-11 text-sm">
                  {{ $t('PILOT.BUILTIN_TOOLS.CONFIG.GUIDE.DESCRIPTION') }}
                </p>
              </div>
            </div>
          </section>

          <!-- 字段白名单配置 -->
          <div
            class="p-4 rounded-xl outline-1 outline outline-n-container bg-n-solid-2"
          >
            <div class="flex items-center gap-2 mb-2">
              <h3 class="text-sm font-medium text-n-slate-12">
                {{ $t('PILOT.BUILTIN_TOOLS.CONFIG.SECTION_TITLE') }}
              </h3>
            </div>
            <p class="text-sm text-n-slate-10 mb-4">
              {{ $t('PILOT.BUILTIN_TOOLS.CONFIG.SECTION_DESCRIPTION') }}
            </p>

            <!-- 添加字段按钮 -->
            <Button
              v-if="!isAtLimit"
              icon="i-lucide-code"
              :label="$t('PILOT.BUILTIN_TOOLS.CONFIG.ADD_FIELD_BUTTON')"
              class="w-full mb-4"
              @click="openAddDrawer"
            />

            <!-- 已选字段列表 -->
            <div>
              <!-- 空状态：假数据 + 渐变蒙层 -->
              <div
                v-if="selectedFields.length === 0"
                class="relative overflow-hidden rounded-lg"
              >
                <!-- 假数据 -->
                <div class="grid gap-3 opacity-50 pointer-events-none p-0.5">
                  <div
                    v-for="field in placeholderFields"
                    :key="field.attribute_key"
                    class="flex gap-3 p-4 rounded-2xl outline outline-1 bg-n-solid-1 outline-n-container"
                  >
                    <!-- 拖动手柄占位 -->
                    <div class="flex-shrink-0 flex items-center text-n-slate-9">
                      <Icon icon="i-lucide-grip-vertical" class="size-4" />
                    </div>
                    <div class="flex-1 flex flex-col gap-2 min-w-0">
                      <div
                        class="flex flex-wrap gap-2 justify-between items-center"
                      >
                        <div class="flex flex-wrap gap-2 items-center min-w-0">
                          <h4
                            class="text-sm font-medium truncate text-n-slate-12"
                          >
                            {{ field.attribute_display_name }}
                          </h4>
                          <div class="w-px h-3 bg-n-strong" />
                          <div
                            class="flex gap-2 items-center text-sm text-n-slate-11"
                          >
                            <div
                              class="flex items-center gap-1.5 text-n-slate-11"
                            >
                              <Icon
                                icon="i-lucide-align-justify"
                                class="size-4"
                              />
                              <span class="text-sm">{{ field.type }}</span>
                            </div>
                            <div class="w-px h-3 bg-n-weak" />
                            <div
                              class="flex items-center gap-1.5 text-n-slate-11"
                            >
                              <Icon icon="i-lucide-code" class="size-4" />
                              <span class="line-clamp-1 text-sm">{{
                                field.value
                              }}</span>
                            </div>
                          </div>
                        </div>
                        <div class="flex gap-2 items-center">
                          <div class="w-px h-3 bg-n-strong" />
                          <div class="h-8 w-8 flex items-center justify-center">
                            <Icon
                              icon="i-lucide-pencil-line"
                              class="size-4 text-n-slate-9"
                            />
                          </div>
                          <div class="w-px h-3 bg-n-strong" />
                          <div class="h-8 w-8 flex items-center justify-center">
                            <Icon
                              icon="i-lucide-trash"
                              class="size-4 text-n-slate-9"
                            />
                          </div>
                        </div>
                      </div>
                      <p class="mb-0 text-sm text-n-slate-11">
                        {{ field.attribute_description }}
                      </p>
                    </div>
                  </div>
                </div>
                <!-- 渐变蒙层 + 提示 -->
                <div
                  class="absolute inset-x-0 bottom-0 bg-gradient-to-t from-n-surface-1 via-n-surface-1/80 to-transparent pt-8 pb-4"
                >
                  <div class="flex flex-col items-center justify-center gap-6">
                    <div
                      class="flex flex-col items-center justify-center gap-3"
                    >
                      <h2
                        class="text-3xl font-medium text-center text-n-slate-12"
                      >
                        {{ $t('PILOT.BUILTIN_TOOLS.CONFIG.EMPTY_TITLE') }}
                      </h2>
                      <p
                        class="text-base text-center text-n-slate-11 tracking-[0.3px]"
                      >
                        {{ $t('PILOT.BUILTIN_TOOLS.CONFIG.EMPTY_SUBTITLE') }}
                      </p>
                    </div>
                    <Button
                      icon="i-lucide-code"
                      :label="$t('PILOT.BUILTIN_TOOLS.CONFIG.ADD_FIELD_BUTTON')"
                      @click="openAddDrawer"
                    />
                  </div>
                </div>
              </div>

              <!-- 已选字段列表（可拖拽排序） -->
              <Draggable
                v-else
                v-model="selectedFields"
                handle=".drag-handle"
                animation="200"
                ghost-class="ghost"
                item-key="attribute_key"
                class="grid gap-3"
                @start="onDragStart"
                @end="onDragEnd"
              >
                <template #item="{ element: field }">
                  <AttributeListItem
                    :attribute="{
                      ...field,
                      label: field.attribute_display_name,
                      type: field.attribute_display_type,
                      value: field.attribute_key,
                      description: field.attribute_description,
                    }"
                    :badges="field.is_builtin ? [{ type: 'builtin' }] : []"
                    :hide-edit="field.is_builtin || field.is_deleted"
                    :is-deleted="field.is_deleted"
                    :show-drag-handle="true"
                    @edit="handleEditAttribute"
                    @delete="handleRemoveField"
                  />
                </template>
              </Draggable>
            </div>
          </div>

          <!-- 添加字段抽屉 -->
          <AddFieldDrawer
            ref="addFieldDrawerRef"
            :available-fields="availableFields"
            :selected-fields="selectedFields"
            :max-fields="MAX_FIELDS"
            @add="handleAddField"
            @batch-add="handleBatchAdd"
          />

          <!-- 提示信息 -->
          <div
            class="bg-n-slate-1 dark:bg-n-slate-7 rounded-2xl border border-n-slate-3 dark:border-n-slate-6 p-4"
          >
            <div class="flex items-start gap-3">
              <Icon
                icon="i-lucide-lightbulb"
                class="size-5 text-n-brand flex-shrink-0 mt-0.5"
              />
              <div class="text-sm text-n-slate-11">
                <p class="font-medium text-n-slate-12 mb-2">
                  {{ $t('PILOT.BUILTIN_TOOLS.CONFIG.TIPS.TITLE') }}
                </p>
                <ul class="list-disc list-inside space-y-1">
                  <li>{{ $t('PILOT.BUILTIN_TOOLS.CONFIG.TIPS.TIP_1') }}</li>
                  <li>{{ $t('PILOT.BUILTIN_TOOLS.CONFIG.TIPS.TIP_2') }}</li>
                  <li>{{ $t('PILOT.BUILTIN_TOOLS.CONFIG.TIPS.TIP_3') }}</li>
                  <li>{{ $t('PILOT.BUILTIN_TOOLS.CONFIG.TIPS.TIP_4') }}</li>
                </ul>
              </div>
            </div>
          </div>
        </template>
      </div>
    </main>

    <!-- 编辑字段弹窗 -->
    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditAttribute
        :selected-attribute="selectedAttribute"
        :is-updating="false"
        @on-close="onAttributeUpdated"
      />
    </woot-modal>
  </div>
</template>

<style scoped>
.ghost {
  opacity: 0.5;
  background: var(--n-surface-3);
}
</style>
